import { Box, Button, Heading, Text } from '@youversion/design-system-components';
import GlobeStroked from '@youversion/design-system-icons/lib/GlobeStroked';
import LockStroked from '@youversion/design-system-icons/lib/LockStroked';
import LanguageSelector from 'components/ui/dialogs/LanguageSelector';
import { LifeChurchLogo } from 'components/ui/icons/LifeChurch';
import { AnimatePresence } from 'framer-motion';
import { useQueryParams } from 'hooks/useQueryParams';
import NextLink from 'next/link';
import { useTranslation } from 'next-i18next';
import { useId, useMemo, useState } from 'react';
import { Screen } from 'src/utils/firebase/types';
import { firebaseLogEvent } from 'utils/firebase';

interface Props {
  screen: Screen;
}

function capitalize(lang: string) {
  return lang.slice(0, 1).toUpperCase() + lang.slice(1);
}

export default function Footer({ screen }: Props) {
  const { i18n, t } = useTranslation('common');
  const { language: currentLanguage } = i18n;
  const [langSelectorOpen, setLangSelectorOpen] = useState(false);
  const { hide_nav: hideNav } = useQueryParams();

  const languageNames = useMemo(() => {
    return new Intl.DisplayNames([currentLanguage], {
      style: 'short',
      type: 'language',
    });
  }, [currentLanguage]);

  return (
    <>
      <footer className='mbs-2 text-center pli-3 md:plb-2 md:pli-1'>
        <Box className='mbd-3 flex items-center justify-center'>
          <LockStroked className='text-gray-50 dark:text-white mie-1' />
          <Box className='mie-2'>
            <Text className='text-gray-25' size='md' variant='caption'>
              {t('giving.titles.value_privacy')}
            </Text>
            <Box className='flex items-start'>
              <NextLink
                className='underline'
                href='https://www.bible.com/privacy'
                onClick={() => firebaseLogEvent('GivingButtonTap', { label: 'Privacy Policy', screen })}
                target='_blank'
              >
                <Text size='md' variant='caption'>
                  {t('giving.titles.privacy')}
                </Text>
              </NextLink>
              <Text className='text-gray-50 dark:text-white'>&nbsp;|&nbsp;</Text>
              <NextLink
                className='underline'
                href='https://www.bible.com/terms'
                onClick={() => firebaseLogEvent('GivingButtonTap', { label: 'Terms', screen })}
                target='_blank'
              >
                <Text size='md' variant='caption'>
                  {t('giving.titles.terms')}
                </Text>
              </NextLink>
            </Box>
          </Box>
        </Box>
        <Box className='flex flex-col md:flex-row justify-center md:justify-between items-center mbs-1 pbs-1'>
          <Box
            aria-label={`${t('giving.titles.digital_ministry')} Life.Church`}
            className='flex flex-row md:flex-wrap no-underline md:gap-1 items-center mli-4'
          >
            <Heading className='!mbe-0' level={3}>
              <Text className='font-bold text-gray-50 uppercase whitespace-nowrap' size='sm' variant='label'>
                {t('giving.titles.digital_ministry')}
              </Text>
            </Heading>
            <LifeChurchLogo
              className='fill-current text-gray-50 dark:text-white'
              color='gray.50'
              height='24px'
              title={t('giving.titles.lifechurch')}
              titleId={useId()}
              width='168px'
            />
          </Box>
          {hideNav ? null : (
            <Box className='mbs-2 md:mbs-0'>
              <Button
                icon={GlobeStroked}
                iconPosition='start'
                id='btn_language_selector'
                onClick={() => {
                  firebaseLogEvent('GivingButtonTap', { label: 'Language', screen });
                  setLangSelectorOpen(true);
                }}
                size='sm'
                variant='stroked'
              >
                {capitalize(languageNames.of(currentLanguage) ?? currentLanguage)}
              </Button>
            </Box>
          )}
        </Box>
      </footer>
      <AnimatePresence>
        {langSelectorOpen ? <LanguageSelector onClose={() => setLangSelectorOpen(false)} /> : null}
      </AnimatePresence>
    </>
  );
}
